import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTwitter,
  FaLinkedin,
  FaGlobe,
} from "react-icons/fa";
import "./AllReports.css";

export default function AllReports() {
  const { companySlug, exchange } = useParams();

  const parts = companySlug?.split("-");
  const ticker = parts?.[parts.length - 1]?.toUpperCase(); // AAPL

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMore, setShowMore] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_URL;

  // navigate(`/company/${slug}`);

  const normalizeUrl = useCallback(
    (url) => {
      if (!url) return null;
      url = url.trim();
      if (url.startsWith("http")) return url;
      return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
    },
    [BASE_URL]
  );

  useEffect(() => {
    if (!ticker) return;

    async function loadData() {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/company-reports/${ticker}/${exchange}/`
        );

        const formattedReports = (res.data.reports || []).map((r) => ({
          ...r,
          report_pdf: normalizeUrl(r.pdf_url),
          thumbnail_url: normalizeUrl(r.thumbnail_url),
        }));

        setData({
          ...res.data,
          logo: normalizeUrl(res.data.logo),
          reports: formattedReports,
        });
      } catch (err) {
        console.error("Error fetching company reports:", err);
        setData(null);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [ticker, exchange, BASE_URL, normalizeUrl]);

  if (!ticker)
    return <p className="no-data-message">Invalid company request.</p>;
  if (loading) return <p className="loading-message">Loading…</p>;
  if (!data) return <p className="no-data-message">Company not found.</p>;

  const {
    company_name,  
    sector,
    industry,
    employee_count,
    address,
    description,
    social_links = {},
    logo,
    reports = [],
    report_message,
    // exchange,
  } = data;

  const mostRecent = reports.length
    ? [...reports].sort((a, b) => (b.year || 0) - (a.year || 0))[0]
    : null;
  const remainingReports = mostRecent
    ? reports.filter((r) => r.id !== mostRecent.id)
    : reports;

  const MIN = 6;
  const visibleReports = showMore
    ? remainingReports
    : remainingReports.slice(0, MIN);

  return (

    <>
      <Helmet>
        <title>
          {company_name
            ? `${company_name} (${ticker}) Annual Report ${mostRecent?.year || ""} PDF Download`
            : "Company Annual Report PDF"}
        </title>

        <meta
          name="description"
          content={
            company_name
              ? `Download ${company_name} (${ticker}) annual report PDF including financial statements, revenue details, and investor insights. Get latest and past reports.`
              : "Download company annual reports in PDF format."
          }
        />

        {/* ❌ not very important but ok to keep */}
        <meta
          name="keywords"
          content={`
      ${company_name} annual report,
      ${company_name} ${mostRecent?.year} report pdf,
      ${ticker} financial report,
      ${exchange} listed companies reports,
      company annual report pdf download,
      investor reports ${company_name}
    `}
        />

        {/* ✅ Canonical URL */}
        <link rel="canonical" href={window.location.href} />

        {/* ✅ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": company_name,
            "url": window.location.href,
            "logo": logo || "",
          })}
        </script>
      </Helmet>
      <div className="page-container">
        <h1 className="main-title">
          {company_name} ({ticker})
        </h1>

        <div className="seo-content">
          {/* <p>
            Download {company_name} ({ticker}) annual report PDF including financial statements,
            revenue analysis, and investor insights. Access latest and previous reports.
          </p> */}
        </div>

        <CompanyInfoCard
          logo={logo}
          exchange={exchange}
          sector={sector}
          industry={industry}
          employee_count={employee_count}
          address={address}
          description={description}
          social_links={social_links}
        />

        {mostRecent ? (
          <MostRecentCard report={mostRecent} company_name={company_name} />
        ) : (
          <div className="no-reports-box">
            <h2>No Reports Available</h2>
            <p>{report_message || "This company does not have any reports."}</p>
          </div>
        )}

        {remainingReports.length > 0 && (
          <>
            <h2 className="section-heading">All Reports</h2>
            <div className="reports-grid">
              {visibleReports.map((r) => (
                <ReportCard key={r.id || r.year} report={r} />
              ))}
            </div>
            {remainingReports.length > MIN && (
              <div className="show-more-container">
                <button
                  className="btn-primary"
                  onClick={() => setShowMore(!showMore)}
                >
                  {showMore ? "Show Less" : "Show More Reports"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ------------------------ Sub Components ------------------------ */

function CompanyInfoCard({
  logo,
  exchange,
  sector,
  industry,
  employee_count,
  address,
  description,
  social_links = {},
}) {
  const [expanded, setExpanded] = useState(false);
  const shortText =
    description && description.length > 200
      ? description.slice(0, 200) + "..."
      : description;

  const { instagram, facebook, youtube, twitter, linkedin, website } = social_links;

  const socialIcons = [
    { link: instagram, icon: <FaInstagram /> },
    { link: facebook, icon: <FaFacebook /> },
    { link: youtube, icon: <FaYoutube /> },
    { link: twitter, icon: <FaTwitter /> },
    { link: linkedin, icon: <FaLinkedin /> },
    { link: website, icon: <FaGlobe /> },
  ];

  return (
    <div className="company-card">
      <img
        src={logo || "/fallback-logo.png"}
        alt="Logo"
        className="company-logo"
        onError={(e) => (e.target.src = "/fallback-logo.png")}
      />
      <div className="company-details">
        {exchange && <p><strong>Exchange:</strong> {exchange}</p>}
        {sector && <p><strong>Sector:</strong> {sector}</p>}
        {industry && <p><strong>Industry:</strong> {industry}</p>}
        {employee_count && <p><strong>Employees:</strong> {employee_count}</p>}
        {address && <p><strong>Address:</strong> {address}</p>}

        {description && (
          <div className="description-section">
            <strong>Description:</strong>
            <p>
              {expanded ? description : shortText}
              {description.length > 200 && (
                <span
                  className="readmore-toggle"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? " Read Less" : " Read More"}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Social Links */}
        <div className="social-links-section">
          <strong>Social Links:</strong>
          <ul>
            {socialIcons.map(
              (s, i) =>
                s.link && (
                  <li key={i}>
                    <a href={s.link} target="_blank" rel="noreferrer">
                      {s.icon}
                    </a>
                  </li>
                )
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function MostRecentCard({ report, company_name }) {
  if (!report) return null;

  return (
    <div className="recent-card">
      <img
        src={report.thumbnail_url || "/fallback-thumb.jpg"}
        alt={`${company_name} ${report.year} Annual Report PDF`}
        className="recent-thumb"
        onError={(e) => (e.target.src = "/fallback-thumb.jpg")}
      />
      <div>
        <h2>Most Recent Report</h2>
        <h3>{report.year} Annual Report</h3>
        <div className="button-row">
          {report.report_pdf && (
            <>
              {/* OPEN */}
              <button
                className="btn-primary"
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_API_URL}/download-report/${report.id}/?mode=view`,
                    "_blank"
                  )
                }
              >
                Open PDF
              </button>

              {/* DOWNLOAD */}
              <button
                className="btn-primary"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = report.report_pdf;
                  link.download = `${report.year}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                Download
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportCard({ report }) {
  const openFile = () => {
    if (report.id) {
      window.open(
        `${process.env.REACT_APP_API_URL}/download-report/${report.id}/?mode=view`,
        "_blank"
      );
    }
  };

  const downloadFile = () => {
    if (report.id) {
      window.location.href =
        `${process.env.REACT_APP_API_URL}/download-report/${report.id}/?mode=download`;
    }
  };

  return (
    <div className="report-card">
      <img
        src={report.thumbnail_url || "/fallback-thumb.jpg"}
        alt="Report"
        className="report-thumb"
        onError={(e) => (e.target.src = "/fallback-thumb.jpg")}
      />
      <h3 className="report-year">Year {report.year}</h3>
      <div className="button-row">
        <button className="btn-small" onClick={openFile}>
          Open
        </button>
        <button className="btn-small" onClick={downloadFile}>
          Download
        </button>
      </div>
    </div>
  );
}
